<template>
  <Modal
    title="Invitar Diseñadores"
    class-name="large"
    :close-icon="closeIcon"
    :is-loading="isLoading"
    :is-visible="true"
  >
    <template v-slot:body>
      <div v-if="!invitesSendSuccess">
        <p class="text-black">
          <span v-html="designerCountMessage"></span>
          <span
            v-if="invite.BasePaymentCount > 0"
            v-html="basePaymentCountMessage"
          >
          </span>
        </p>
        <Callout
          v-if="isTooManyParticipationPayments"
          type="warning"
          :icon-url="warningIcon"
          title="Demasiados pagos por participación asignados"
          :closable="true"
          @close="isTooManyParticipationPayments = false"
        >
          <template v-slot:message>
            Únicamente tienes {{ invite.BasePaymentCount }} pagos por participación para asignar. Remueve el pago por participación de otro diseñador para poder asignar.
          </template>
        </Callout>
        <div v-if="inviteData" class="table-scroll-modal">
          <table class="table stack">
            <thead class="hide-for-small-only hide-for-medium-only">
              <tr>
                <th>Diseñador</th>
                <th>¿Enviar invitación?</th>
                <th v-if="invite.BasePaymentCount > 0">
                  ¿Enviar pago por participación?
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(designer, index) in inviteData.Designers"
                :key="index"
              >
                <td>
                  <div class="user-profile user-profile--tiny mhn">
                    <div class="row collapse align-middle mhn">
                      <div class="column shrink">
                        <div
                          v-if="designer.ProfilePicUrl"
                          class="user-profile__image"
                        >
                          <div class="responsive-embed square">
                            <img
                              class="responsive-embed__image"
                              height="40"
                              width="40"
                              :src="designer.ProfilePicUrl"
                              alt="Profile picture"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="column">
                        <div class="row">
                          <div class="column small-12">
                            <span class="user-profile__small-text">
                              <a :href="designer.PortfolioUrl">
                                {{ designer.DisplayName }}
                              </a>
                            </span>
                          </div>
                          <div
                            v-if="designer.CountryName"
                            class="column small-12"
                          >
                            <span class="user-profile__small-text">
                              {{ designer.CountryName }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <Checkbox
                    :id="`invite-designer-${designer.DesignerId}`"
                    :checked="invitedDesigners[index].isInvited"
                    @input="onInviteDesignerChecked($event, index)"
                  >
                    Invitar al proyecto
                  </Checkbox>
                </td>
                <td v-if="invite.BasePaymentCount">
                  <Checkbox
                    :id="`send-participation-payment-${designer.DesignerId}`"
                    :checked="invitedDesigners[index].isPaidInvite"
                    @input="onParticipationPaymentChecked($event, index)"
                  >
                    Pago por participación
                  </Checkbox>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="text-center">
          <Checkbox id="reallocate-invites" @input="reallocateInvites = $event">
            Redistribuir las invitaciones si son rechazadas
          </Checkbox>
        </div>
      </div>
      <div v-else>
        <Callout
          type="success"
          :icon-url="successIcon"
          title="Invitación Enviada Exitósamente"
          @close="isTooManyParticipationPayments = false"
        >
          <template v-slot:message>
            Hemos enviado {{ invitedDesignersCount }} invitaciones en tu nombre.
          </template>
        </Callout>
        <p>
          También puedes seleccionar y enviar invitaciones a los diseñadores de nuestro directorio de diseñadores. Para hacer esto simplemente:
        </p>
        <ol>
          <li>
            <span v-html="designerPortfolioMessage"></span>
          </li>
          <li>
            Encuentra un diseñador al que quieres invitar personalmente a tu proyecto
          </li>
          <li>
            <span v-html="getADesignFromMeMessage"></span>
            <img
              :src="inviteDesignerPortfolioBanner"
              alt="Invite Designer Portfolio Banner"
            />
          </li>
          <li>
            <span v-html="forMoreInfoMessage"></span>
          </li>
        </ol>
      </div>
    </template>
    <template v-slot:footer>
      <div v-if="!invitesSendSuccess">
        <input
          type="submit"
          :class="{
            ['button small mrm']: true,
            disabled: invitedDesignersCount === 0,
          }"
          value="Enviar invitaciones"
          @click="onClickSubmitInvites"
        />
        <button
          class="button secondary hollow small"
          data-close
          data-role="cancel-invite-wizard"
        >
          Cancelar
        </button>
      </div>
      <div v-else>
        <button class="button small" data-close>Okay, Entendido</button>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '../../../shared/Modal/Modal.vue';
import Checkbox from '../../../shared/Checkbox/Checkbox.vue';
import Callout from '../../../shared/Callout/Callout.vue';
import submitInvitesAsync from '../../../../clients/invites-api';

export default {
  components: {
    Modal,
    Checkbox,
    Callout,
  },
  props: {
    warningIcon: {
      type: String,
      default: undefined,
    },
    successIcon: {
      type: String,
      default: undefined,
    },
    invite: {
      type: Object,
      required: true,
    },
    closeIcon: {
      type: String,
      default: undefined,
    },
    inviteDesignerPortfolioBanner: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      invitedDesigners: [],
      inviteData: null,
      isTooManyParticipationPayments: false,
      reallocateInvites: false,
      invitesSendSuccess: false,
      isLoading: false,
    };
  },
  computed: {
    designerCount() {
      return this.invite.Designers.length;
    },
    invitedDesignersCount() {
      return this.invitedDesigners.filter((payment) => payment.isInvited)
        .length;
    },
    briefId() {
      const urlMatch = this.$route
        ? this.$route.fullPath.match(/([^/]+$)/)
        : null;

      if (urlMatch && urlMatch.length > 1) {
        return parseInt(urlMatch[1], 10);
      }

      return null;
    },
    designerCountMessage() {
      return `Recomendamos invitar a los siguientes strong>${this.designerCount} diseñadores</strong> a tu proyecto.`;
    },
    basePaymentCountMessage() {
      return `Puedes asignar <strong>${this.invite.BasePaymentCount} pagos de participación (invitaciones pagadas) para atraer a los mejores diseñadores a trabajar en tu proyecto.</strong>`;
    },
    getADesignFromMeMessage() {
      return `Haz clic en <strong>solicítame un diseño</strong> en el portafolio de un diseñador y sigue las instrucciones`;
    },
    forMoreInfoMessage() {
      return `Para más información ver <a href='${this.invite.ForMoreInfoUrl}'> ¿puedo invitar a diseñadores a mi proyecto?</a>`;
    },
    designerPortfolioMessage() {
      return `Ir al portafolio del diseñador - haz clic en el enlace de <a href='${this.invite.DesignersListUrl}'>diseñadores</a> en la barra de navegación de DesignCrowd`;
    },
  },
  mounted() {
    this.initialiseInvitedDesigners();
    this.inviteData = this.invite;
  },
  methods: {
    initialiseInvitedDesigners() {
      this.invitedDesigners = this.invite.Designers.map((designer) => ({
        designerId: designer.DesignerId,
        isPaidInvite: designer.RecommendedForPaidInvite,
        isInvited: true,
      }));
    },
    onInviteDesignerChecked(checked, index) {
      this.$set(this.invitedDesigners[index], 'isInvited', checked);
    },
    onParticipationPaymentChecked(checked, index) {
      this.$set(this.invitedDesigners[index], 'isPaidInvite', checked);
      const checkedPayments = this.invitedDesigners.filter(
        (x) => x.isPaidInvite,
      );

      if (
        checkedPayments &&
        checkedPayments.length > this.inviteData.BasePaymentCount
      ) {
        this.$nextTick(() => {
          this.$set(this.invitedDesigners[index], 'isPaidInvite', false);
          this.isTooManyParticipationPayments = true;
        });
      } else {
        this.isTooManyParticipationPayments = false;
      }
    },
    async onClickSubmitInvites() {
      if (this.invitedDesignersCount > 0) {
        const invites = {
          autoReallocate: this.reallocateInvites,
          designers: this.invitedDesigners.filter(
            (designer) => designer.isInvited,
          ),
        };

        this.isLoading = true;
        const res = await submitInvitesAsync(this.briefId, invites);
        this.invitesSendSuccess = res;
        this.isLoading = false;
      }
    },
  },
};
</script>
